import { AxiosRequestConfig, AxiosPromise, AxiosResponse, AxiosError } from 'axios';
import shortid from 'shortid';
import ApiProvider from './apis/ApiProvider';
import XamarinEventApi from './events/api/XamarinEventApi';
import Logger from '@/common/logging/Logger';

interface IXamarinRequest {
    requestId: string;
    authorization?: string;
    legacyToken?: string;
    data: any;
    url?: string;
}

interface IXamarinResponse {
    requestId: string;
    response: any;
    success: boolean;
    data: any;
    errorMessage: string;
}

interface IXamarinPromise {
    resolve: (input: IXamarinResponse) => void;
    reject: (error: any) => void;
}

export class XamarinAdapter {
    public static instance = new XamarinAdapter();

    public static isXamarin(): boolean {
        if (typeof (window as any).jsBridge !== 'undefined' || location.href.startsWith('file://') || location.href.indexOf('xamarin') > -1) {
            // console.log('isXamarin: true');
            XamarinAdapter.instance.logger.debug('isXamarin: true');
            return true;
        }
        // console.log('isXamarin:', {index: location.href.indexOf('xamarin=true'), href: location.href});
        XamarinAdapter.instance.logger.debug('isXamarin: false');
        return false;
    }

    private logger = Logger('xamarin');
    private cachedMessages: string[] = [];
    private xamarinPromises: {[key: string]: IXamarinPromise } = {};

    constructor() {
        (document as any).ExternalCommunicationService = this; // We need this reference in order for webAppInput to work
        (document as any).webAppInput = (data: string) => {
            (document as any).ExternalCommunicationService.incomming(data);
        };
    }



    public axiosAdapter(config: AxiosRequestConfig): AxiosPromise<any> {
        return new Promise((resolve, reject) => {
            const request: IXamarinRequest = {
                requestId: shortid.generate(),
                authorization: config.headers.Authorization,
                data: config.data || '{}',
                url: config.url,
            };
            XamarinAdapter.instance.xamarinPromises[request.requestId] = {
                resolve: (data: IXamarinResponse) => {
                    resolve({
                        config,
                        data: data.response,
                        status: 200,
                        statusText: 'OK',
                    } as AxiosResponse);
                },
                reject: (error: any) => reject({
                    config,
                    isAxiosError: false,
                    message: error,
                } as AxiosError),
            };

            const base64Request = XamarinAdapter.instance.b64EncodeUnicode(JSON.stringify(request));
            const jsBridge = ((window as any).jsBridge as any);
            if (jsBridge) {
                // XamarinAdapter.instance.logger.debug(`Calling xamarin: ${base64Request}`);
                jsBridge.invokeAction(base64Request);
            } else {
                XamarinAdapter.instance.logger.info('Saving cached message, as jsBridge is not yet defined');
                XamarinAdapter.instance.cachedMessages.push(base64Request);
                if (XamarinAdapter.instance.cachedMessages.length === 1) {
                    setTimeout(XamarinAdapter.instance.lookForjsBridge, 100);
                }
            }
        });
    }

    public incomming(data: string) {
        const obj = JSON.parse(this.b64DecodeUnicode(data));
        // console.log('incomming', {aw: data, transformed: obj});
        if (obj.event) {
            const xamarinEventApi = ApiProvider.getEventApi() as XamarinEventApi;
            xamarinEventApi.newEvent(obj as {type: string, event: any});
        } else {
            const request = XamarinAdapter.instance.xamarinPromises[obj.requestId];
            if (request) {
                if (obj.success) {
                    request.resolve(obj);
                } else {
                    request.reject(obj.error);
                }
            }
        }
        // const obj = JSON.parse(this.b64DecodeUnicode(data)) as IXamarinResponse;

    }

    private lookForjsBridge() {
        const jsBridge = ((window as any).jsBridge as any);
        if (jsBridge) {
            if (XamarinAdapter.instance.cachedMessages.length > 0) {
                XamarinAdapter.instance.logger.info(`Pushing ${XamarinAdapter.instance.cachedMessages.length} cached messages`);
                while (XamarinAdapter.instance.cachedMessages.length > 0) {
                    const message = XamarinAdapter.instance.cachedMessages.pop();
                    jsBridge.invokeAction(message);
                }
            }
        } else {
            setTimeout(XamarinAdapter.instance.lookForjsBridge, 100);
        }
    }

    private b64EncodeUnicode(str: string): string {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
            return String.fromCharCode(parseInt(p1, 16));
        }));
    }

    private b64DecodeUnicode(str: string): string {
        return decodeURIComponent(Array.prototype.map.call(atob(str),
            (c) => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
    }
}
(window as any).test = XamarinAdapter;
